import { Platform } from 'react-native';

import { svaraApi } from '../utils/axiosInstance';

/**
 * Chip and VIP donations: one player pays, another receives, and the first delivered donation
 * unlocks a private chat between the two. Mirrors the gateway-game endpoints in
 * `DONATIONS-CONTRACT.md` (trinity-nest `apps/gateway-game/src/store` and `.../chat`). Paying
 * itself goes through the existing rails with an extra `recipientPlayerId`: the web deposit body
 * (`packagesSagas`) and the in-app purchase verify payload (`iapSagas`, chips only).
 */
export type DonationRecipient = { playerId: string; username: string };

export type DonationPlatform = 'WEB' | 'IOS' | 'ANDROID';

export interface DonationCheck {
	allowed: boolean;
	/** Why the donation is refused, already worded for the player; null when allowed. */
	reason: string | null;
	recipientUsername: string | null;
}

/** The profile's view of a pair: whether a delivered donation has opened the chat yet. */
export interface ChatStateWithPlayer {
	conversationId: string | null;
	unlocked: boolean;
}

/**
 * What the async web result needs to know about a deposit that was a donation. Written next to
 * `package_{depositId}` when the deposit is created, read back with it when the checkout tab
 * reports in (`usePaymentStatus`).
 */
export interface DonationContext {
	recipientPlayerId: string;
	recipientUsername: string;
	type: 'CHIPS' | 'VIP';
	chipAmount?: number;
	vipDurationDays?: number;
}

export const donationContextKey = (depositId: string) => `donation_${depositId}`;

export const getDonationPlatform = (): DonationPlatform =>
	Platform.OS === 'ios' ? 'IOS' : Platform.OS === 'android' ? 'ANDROID' : 'WEB';

/** Must run before any payment: the app stores charge before the backend ever sees the purchase. */
export async function checkDonation(params: {
	recipientPlayerId: string;
	packageId: string;
}): Promise<DonationCheck> {
	const response = await svaraApi.get<DonationCheck>('/api/store/donations/check', {
		params: { ...params, platform: getDonationPlatform() },
	});
	return response.data;
}

export async function getChatStateWithPlayer(playerId: string): Promise<ChatStateWithPlayer> {
	const response = await svaraApi.get<ChatStateWithPlayer>(
		`/api/chat/conversations/with/${encodeURIComponent(playerId)}`,
	);
	return response.data;
}

// A delivered donation changes the profile it was made from (the chat unlocks), but the result
// arrives far from that profile: the in-app purchase verify inside the store, or on web a
// checkout tab reporting back to the main one. Profiles listen here to refresh their chat state.
type DonationCompletedListener = (recipientPlayerId: string) => void;
const donationCompletedListeners = new Set<DonationCompletedListener>();

export function onDonationCompleted(listener: DonationCompletedListener): () => void {
	donationCompletedListeners.add(listener);
	return () => {
		donationCompletedListeners.delete(listener);
	};
}

export function notifyDonationCompleted(recipientPlayerId: string) {
	donationCompletedListeners.forEach(listener => listener(recipientPlayerId));
}
